import pdfMake from 'pdfmake/build/pdfmake';
import htmlToPdfmake from 'html-to-pdfmake';
import { getDownloadContent, getResultContent } from './Utility';

/**
 * Wraps HTML content with proper styling for PDF generation
 * @param {string} fullHtml - The HTML content to wrap
 * @param {object} data - Data object containing entity_name and current_year
 * @returns {string} - Formatted HTML with styles
 */
export const beautifyWrapper = (fullHtml, data) => {
  const formattedHtml = `
<!DOCTYPE html>
<html>
  <head>
    <meta charset="UTF-8">
    <title>${data.entity_name} ${data.current_year}</title>
    <style>
      /* Page Setup */
      @page {
        size: A4;
        margin: 10mm;
      }

      @media print {
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }

        /* Prevent elements from being cut off */
        h1, h2, h3, h4, h5, h6 {
          page-break-after: avoid;
          break-after: avoid;
        }

        p, li, blockquote {
          page-break-inside: avoid;
          break-inside: avoid;
        }

        /* Allow tables to break across pages */
        table {
          page-break-inside: auto;
          break-inside: auto;
        }

        tr {
          page-break-inside: avoid;
          break-inside: avoid;
        }

        thead {
          display: table-header-group;
        }

        tfoot {
          display: table-footer-group;
        }

        /* Prevent orphans and widows */
        p {
          orphans: 3;
          widows: 3;
        }
      }

      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }

      body {
        margin: 0;
        padding: 15mm;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
        line-height: 1.5;
        color: #333;
        font-size: 11px; /* Smaller font for better fit */
      }

      /* Typography */
      h1 {
        font-size: 20px;
        margin-bottom: 15px;
        color: #1a1a1a;
      }

      h2 {
        font-size: 16px;
        margin-top: 20px;
        margin-bottom: 10px;
        color: #2c3e50;
      }

      h3 {
        font-size: 14px;
        margin-top: 15px;
        margin-bottom: 8px;
        color: #34495e;
      }

      h4 {
        font-size: 12px;
        margin-top: 12px;
        margin-bottom: 6px;
      }

      p {
        margin-bottom: 10px;
      }

      /* Lists */
      ul, ol {
        margin: 10px 0 10px 20px;
      }

      li {
        margin-bottom: 4px;
      }

      /* Table Styles - KEY FIXES */
      table {
        border-collapse: collapse;
        width: 100%;
        margin: 15px 0;
        table-layout: fixed; /* Forces table to fit within width */
        font-size: 9px; /* Smaller font for tables */
      }

      th {
        padding: 4px 6px;
        border: 1px solid #ddd;
        background-color: #f5f5f5;
        text-align: left;
        font-weight: 600;
        word-wrap: break-word;
        overflow-wrap: break-word;
      }

      td {
        padding: 4px 6px;
        border: 1px solid #ddd;
        text-align: left;
        word-wrap: break-word;
        overflow-wrap: break-word;
      }

      tr {
        border-bottom: 1px solid #ddd;
      }

      tr:nth-child(even) {
        background-color: #fafafa;
      }

      /* Code */
      pre {
        background: #f6f8fa;
        padding: 10px;
        border-radius: 4px;
        overflow-x: auto;
        margin: 10px 0;
        border: 1px solid #e1e4e8;
        white-space: pre-wrap; /* Wrap long lines */
        word-wrap: break-word;
        font-size: 9px;
      }

      code {
        font-family: 'SF Mono', Consolas, 'Courier New', monospace;
        font-size: 9px;
        background: #f0f0f0;
        padding: 1px 4px;
        border-radius: 3px;
      }

      pre code {
        background: none;
        padding: 0;
      }

      /* Blockquote */
      blockquote {
        border-left: 3px solid #007bff;
        padding: 8px 15px;
        margin: 15px 0;
        background: #f8f9fa;
        color: #555;
      }

      /* Horizontal Rule */
      hr {
        border: none;
        border-top: 1px solid #ddd;
        margin: 20px 0;
      }

      /* Links */
      a {
        color: #007bff;
        text-decoration: none;
      }

      /* Images */
      img {
        max-width: 100%;
        height: auto;
      }

      /* Utility class for page breaks */
      .page-break {
        page-break-before: always;
        break-before: always;
      }
    </style>
  </head>
  <body>
    ${fullHtml}
  </body>
</html>
`;

  return formattedHtml;
};

/**
 * Downloads results as a rich PDF using browser print dialog
 * @param {string} fullHtml - The HTML content to print
 * @param {object} data - Data object containing entity_name and current_year
 * @param {function} onComplete - Callback when download is complete
 */
export const downloadRichPdf = (fullHtml, data, onComplete) => {
  const formattedHtml = beautifyWrapper(fullHtml, data);

  // Create hidden iframe
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = 'none';
  iframe.style.visibility = 'hidden';

  document.body.appendChild(iframe);
  // Set content using srcdoc
  iframe.srcdoc = formattedHtml;

  iframe.onload = () => {
    setTimeout(() => {
      try {
        iframe.contentWindow.focus();
        // Use afterprint event to properly handle cleanup after print dialog closes
        iframe.contentWindow.addEventListener('afterprint', () => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
          if (onComplete) onComplete();
        });
        iframe.contentWindow.print();
      } catch (e) {
        console.error('Print error:', e);
        // Cleanup on error
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
        if (onComplete) onComplete();
      }
    }, 500);
  };
};

/**
 * Downloads results as a rich PDF in a new window
 * @param {string} fullHtml - The HTML content to print
 * @param {object} data - Data object containing entity_name and current_year
 */
export const downloadRichPdfNewWindow = (fullHtml, data) => {
  const formattedHtml = beautifyWrapper(fullHtml, data);

  const blob = new Blob([formattedHtml], { type: 'text/html;charset=utf-8' });
  const blobUrl = URL.createObjectURL(blob);
  const printWindow = window.open(blobUrl, '_blank');

  // Guard against popup being blocked by browser
  if (!printWindow) {
    URL.revokeObjectURL(blobUrl);
    console.error('Popup blocked by browser');
    return;
  }

  printWindow.addEventListener('load', () => {
    printWindow.print();
  });

  printWindow.addEventListener('afterprint', () => {
    URL.revokeObjectURL(blobUrl); // Cleanup
  });
};

/**
 * Initialize pdfMake with fonts
 */
export const initPdfMake = async () => {
  try {
    const pdfFonts = await import("pdfmake/build/vfs_fonts");

    if (pdfFonts.pdfMake?.vfs) {
      pdfMake.vfs = pdfFonts.pdfMake.vfs;
    } else if (pdfFonts.default?.pdfMake?.vfs) {
      pdfMake.vfs = pdfFonts.default.pdfMake.vfs;
    } else if (pdfFonts.vfs) {
      pdfMake.vfs = pdfFonts.vfs;
    } else {
      console.error("Could not find vfs fonts");
    }
  } catch (error) {
    console.error("Error loading pdfMake fonts:", error);
  }
};

/**
 * Downloads results as a plain PDF using pdfMake
 * @param {string} fullHtml - The HTML content to convert
 * @param {object} data - Data object containing entity_name and current_year
 * @param {function} onComplete - Callback when download is complete
 */
export const downloadPlainPdf = async (fullHtml, data, onComplete) => {
  await initPdfMake();
  // Convert HTML to pdfmake format - with selectable text. Issue is, it wont print special characters
  const pdfContent = htmlToPdfmake(fullHtml);

  const docDefinition = {
    content: pdfContent,
    defaultStyle: {
      fontSize: 12,
    },
    styles: {
      'html-th': {
        fillColor: '#f5f5f5',
        bold: true,
      },
    },
    pageSize: 'A4',
    pageMargins: [40, 40, 40, 40],
  };

  pdfMake.createPdf(docDefinition).download(
    `${data.entity_name}_${data.current_year}.pdf`,
    () => { if (onComplete) onComplete(); }
  );
};

/**
 * Downloads FirstPass results for a given data object
 * @param {object} data - The data object with response_s3_path, entity_name, current_year
 * @param {function} onStart - Callback when download starts
 * @param {function} onComplete - Callback when download completes
 * @param {string} type - 'rich' for browser print dialog, 'plain' for pdfMake
 * @param {string} viewOption - The current view option ('exception' or 'all')
 */
export const downloadFirstPassResults = async (data, onStart, onComplete, type = 'rich', viewOption) => {
  if (onStart) onStart();
  
  try {
    const mark = await getDownloadContent(data, viewOption);
    
    // Guard against null/undefined content
    if (!mark?.html) {
      console.error('No result content found');
      if (onComplete) onComplete();
      return;
    }
    
    if (type === 'rich') {
      downloadRichPdf(mark.html, data, onComplete);
    } else {
      await downloadPlainPdf(mark.html, data, onComplete);
    }
  } catch (error) {
    console.error('Error downloading results:', error);
    if (onComplete) onComplete();
  }
};
